import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule, FormBuilder, FormArray, Validators, AbstractControl } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { DropdownModule } from 'primeng/dropdown';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';
import { OnboardingService } from '../../../services/onboarding/onboarding.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FieldValidators } from '../../shared/validators/field-validators';
import { FieldErrorComponent } from '../../shared/components/field-error/field-error.component';
import { isFieldInvalid,validateFile } from '../../shared/validators/form-validation.utils';

@Component({
  selector: 'app-da-awards',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    InputTextareaModule,
    DropdownModule,
    SelectButtonModule,
    ButtonModule,
    ToastModule,
    DialogModule,
    FieldErrorComponent
  ],
  providers: [MessageService],
  templateUrl: './awards.component.html',
  styleUrl: './awards.component.scss'
})
export class DaAwardsComponent implements OnInit {
  @Input() parentForm!: FormGroup;
  @Output() loaded = new EventEmitter<void>();

  displayPreviewDialog: boolean = false;
  safePdfUrl: SafeResourceUrl | null = null;
  previewFileName: string = '';
  originalFileUrl: string = '';
  isPdfLoading: boolean = false;
  isSavingMap: { [key: number]: boolean } = {};
  isSavingNoAwards: boolean = false;
  hasNoAwardsRecordId: number | null = null;

  // Delete dialog state
  showDeleteDialog = false;
  isDeleting = false;
  pendingDeleteIndex: number | null = null;

  yesNoOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  categoryOptions = [
    { label: 'Sports', value: 1 },
    { label: 'Academic', value: 2 },
    { label: 'Leadership', value: 3 },
    { label: 'Social Work', value: 4 },
    { label: 'Competitions', value: 5 },
    { label: 'Others', value: 6 }
  ];

  levelOptions = [
    { label: 'School / Local', value: 1 },
    { label: 'State / Regional', value: 2 },
    { label: 'National', value: 3 },
    { label: 'International', value: 4 }
  ];

  constructor(
    private fb: FormBuilder,
    private onboardingService: OnboardingService,
    private sanitizer: DomSanitizer,
    private messageService: MessageService
  ) { }

  ngOnInit() {
    if (!this.parentForm.get('hasAwards')) {
      this.parentForm.addControl('hasAwards', this.fb.control(null, Validators.required));
    }
    if (!this.parentForm.get('awards')) {
      this.parentForm.addControl('awards', this.fb.array([]));
    }

    this.parentForm.get('hasAwards')?.valueChanges.subscribe(val => {
      if (val) {
        this.awards.enable();
        if (this.awards.length === 0) {
          this.addAward();
        }
      } else {
        this.awards.disable();
        this.awards.clear();
      }
      // Do NOT markAsPristine here — we need it dirty so Save button shows the unsaved warning
    });

    this.loadAwardsData();
  }

  get awards(): FormArray {
    return this.parentForm.get('awards') as FormArray;
  }

  loadAwardsData() {
    this.onboardingService.getAwards().subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : (res?.result || []);
        if (list && list.length > 0) {
          // Separate real awards from the "No" placeholder
          const realEntries = list.filter((r: any) => r.hasAwardOrAchievements !== false);
          const noPlaceholder = list.find((r: any) => r.hasAwardOrAchievements === false);

          if (realEntries.length > 0) {
            // User has real awards — show Yes and load them
            this.parentForm.get('hasAwards')?.setValue(true);
            this.awards.clear();
            realEntries.forEach((record: any) => {
              this.addAward(record);
            });
            this.hasNoAwardsRecordId = null;
          } else if (noPlaceholder) {
            // Only a "No" placeholder exists
            this.parentForm.get('hasAwards')?.setValue(false);
            this.awards.clear();
            this.hasNoAwardsRecordId = noPlaceholder.id ?? noPlaceholder.Id;
          } else {
            this.parentForm.get('hasAwards')?.setValue(false);
            this.awards.clear();
            this.hasNoAwardsRecordId = null;
          }
          this.parentForm.get('hasAwards')?.markAsPristine();
          this.awards.markAsPristine();
        } else {
          this.parentForm.get('hasAwards')?.setValue(false);
          this.parentForm.get('hasAwards')?.markAsPristine();
          this.hasNoAwardsRecordId = null;
        }
        this.loaded.emit();
      },
      error: (err) => {
         if (this.awards.length === 0) {
          this.addAward();
        }
        this.loaded.emit();
      }
    });

    // Auto-add a blank award when user toggles to Yes
    this.parentForm.get('hasAwards')?.valueChanges.subscribe((val: boolean) => {
      if (val && this.awards.length === 0) {
        this.addAward();
      }
    });
  }

  addAward(data?: any) {
    const group = this.fb.group({
      id: [data?.id || null],
      category: [data?.achievementType || data?.AchievementType ? Number(data?.achievementType || data?.AchievementType) : null, FieldValidators.requiredOnly],
      awardName: [data?.awardName || data?.AwardName || '', FieldValidators.alphanumericText],
      level: [data?.achievementLevel || data?.Level || data?.level || null, FieldValidators.requiredOnly],
      issuingBody: [data?.issuingBody || data?.IssuingBody || '', FieldValidators.alphanumericTextOptional],
      organisation: [data?.organisationOrCause || data?.OrganisationOrCause || '', FieldValidators.alphanumericTextOptional],
      impact: [data?.impact || data?.Impact || '', FieldValidators.optionalMax500],
      responsibility: [data?.responsibility || data?.Responsibility || '', FieldValidators.optionalMax500],
      keyAchievements: [data?.keyAchievements || data?.KeyAchievements || '', FieldValidators.optionalMax500],
      specify: [data?.otherSpecification || data?.OtherSpecification || ''],
      description: [data?.description || data?.Description || '', FieldValidators.description500],
      fileUrl: [data?.documentUrl || data?.DocumentUrl || null]
    });

    group.get('category')?.valueChanges.subscribe(val => {
      const issuingCtrl = group.get('issuingBody');
      const orgCtrl = group.get('organisation');
      const impactCtrl = group.get('impact');
      const respCtrl = group.get('responsibility');
      const achCtrl = group.get('keyAchievements');
      const specCtrl = group.get('specify');

      issuingCtrl?.clearValidators();
      orgCtrl?.clearValidators();
      impactCtrl?.clearValidators();
      respCtrl?.clearValidators();
      achCtrl?.clearValidators();
      specCtrl?.clearValidators();

      if (val === 1) {
        issuingCtrl?.setValidators(FieldValidators.alphanumericText);
      } else if (val === 4) {
        orgCtrl?.setValidators(FieldValidators.alphanumericText);
        impactCtrl?.setValidators(FieldValidators.description500);
      } else if (val === 3) {
        respCtrl?.setValidators(FieldValidators.description500);
        achCtrl?.setValidators(FieldValidators.description500);
      } else if (val === 6) {
        specCtrl?.setValidators(FieldValidators.requiredOnly);
      }

      issuingCtrl?.updateValueAndValidity({ emitEvent: false });
      orgCtrl?.updateValueAndValidity({ emitEvent: false });
      impactCtrl?.updateValueAndValidity({ emitEvent: false });
      respCtrl?.updateValueAndValidity({ emitEvent: false });
      achCtrl?.updateValueAndValidity({ emitEvent: false });
      specCtrl?.updateValueAndValidity({ emitEvent: false });
    });

    const initialCat = group.get('category')?.value;
    if (initialCat === 1) {
      group.get('issuingBody')?.setValidators(FieldValidators.alphanumericText);
      group.get('issuingBody')?.updateValueAndValidity({ emitEvent: false });
    } else if (initialCat === 4) {
      group.get('organisation')?.setValidators(FieldValidators.alphanumericText);
      group.get('organisation')?.updateValueAndValidity({ emitEvent: false });
      group.get('impact')?.setValidators(FieldValidators.description500);
      group.get('impact')?.updateValueAndValidity({ emitEvent: false });
    } else if (initialCat === 3) {
      group.get('responsibility')?.setValidators(FieldValidators.description500);
      group.get('responsibility')?.updateValueAndValidity({ emitEvent: false });
      group.get('keyAchievements')?.setValidators(FieldValidators.description500);
      group.get('keyAchievements')?.updateValueAndValidity({ emitEvent: false });
    } else if (initialCat === 6) {
      group.get('specify')?.setValidators(FieldValidators.requiredOnly);
      group.get('specify')?.updateValueAndValidity({ emitEvent: false });
    }

    this.awards.push(group);
  }

  saveNoAwards() {
    const hasAwardsControl = this.parentForm.get('hasAwards');
    if (!hasAwardsControl?.dirty) {
      this.messageService.add({ severity: 'info', summary: 'No Changes', detail: 'No changes detected. Nothing to update.' });
      return;
    }

    this.isSavingNoAwards = true;
    const formData = new FormData();
    formData.append('HasAwardOrAchievements', 'false');
    formData.append('AwardName', '');
    formData.append('Description', '');

    this.onboardingService.createAward(formData).subscribe({
      next: (res: any) => {
        const newId = res?.id || res?.Id || res?.result?.id || res?.result?.Id;
        if (newId) {
          this.hasNoAwardsRecordId = newId;
        }
        hasAwardsControl.markAsPristine();
        this.isSavingNoAwards = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Awards preference saved successfully.' });
      },
      error: () => {
        hasAwardsControl.markAsPristine();
        this.isSavingNoAwards = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Awards preference saved successfully.' });
      }
    });
  }

  removeAward(index: number) {
    const id = this.awards.at(index).get('id')?.value;
    if (!id) {
      this.awards.removeAt(index);
      return;
    }
    this.pendingDeleteIndex = index;
    this.showDeleteDialog = true;
  }

  confirmDelete() {
    if (this.pendingDeleteIndex === null) return;
    const index = this.pendingDeleteIndex;
    const id = this.awards.at(index).get('id')?.value;
    this.isDeleting = true;
    this.onboardingService.deleteAward(id).subscribe({
      next: () => {
        this.isDeleting = false;
        this.showDeleteDialog = false;
        this.pendingDeleteIndex = null;
        this.awards.removeAt(index);
        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Award removed successfully.' });
      },
      error: () => {
        this.isDeleting = false;
        this.showDeleteDialog = false;
        this.pendingDeleteIndex = null;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete award.' });
      }
    });
  }

  cancelDelete() {
    this.showDeleteDialog = false;
    this.pendingDeleteIndex = null;
  }

  saveAward(index: number) {
    const awGroup = this.awards.at(index) as FormGroup;
    awGroup.markAllAsTouched();
    if (awGroup.invalid) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields.' });
      this.scrollToFirstError();
      return;
    }

    const val = awGroup.value;
    const existingId = val.id;

    const formData = new FormData();
    if (existingId) {
      formData.append('Id', existingId.toString());
    }

    formData.append('HasAwardOrAchievements', 'true');
    formData.append('AchievementType', val.category?.toString() || '');
    formData.append('AwardName', val.awardName || '');
    formData.append('AchievementLevel', val.level?.toString() || '');
    formData.append('IssuingBody', val.issuingBody || '');
    formData.append('OrganisationOrCause', val.organisation || '');
    formData.append('Impact', val.impact || '');
    formData.append('Responsibility', val.responsibility || '');
    formData.append('KeyAchievements', val.keyAchievements || '');
    formData.append('OtherSpecification', val.specify || '');
    formData.append('Description', val.description || '');

    const selectedFile = (awGroup as any)._selectedFile;
    if (selectedFile) {
      formData.append('documentUrl', selectedFile);
    }

    if (existingId) {

      if (!awGroup.dirty && !selectedFile) {
        this.messageService.add({ severity: 'info', summary: 'No Changes', detail: 'No changes detected. Nothing to update.' });
        return;
      }

      this.isSavingMap[index] = true;
      this.onboardingService.updateAward(existingId, formData).subscribe({
        next: (res: any) => {
          this.isSavingMap[index] = false;
          this.messageService.add({ severity: 'success', summary: 'Updated', detail: 'Award updated successfully.' });
          awGroup.markAsPristine();
          this.parentForm.get('hasAwards')?.markAsPristine();
        },
        error: (err) => {
          this.isSavingMap[index] = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update award.' });
        }
      });
    } else {
      this.isSavingMap[index] = true;
      this.onboardingService.createAward(formData).subscribe({
        next: (res: any) => {
          this.isSavingMap[index] = false;
          const newId = res?.id || res?.result?.id;
          if (newId) {
            awGroup.get('id')?.setValue(newId);
          }
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Award saved successfully.' });
          awGroup.markAsPristine();
          this.parentForm.get('hasAwards')?.markAsPristine();
        },
       error: (err) => {
  this.isSavingMap[index] = false;
  const backendMsg = err?.error?.errors?.documentUrl?.[0] || 'Failed to update award.';
  this.messageService.add({ severity: 'error', summary: 'Error', detail: backendMsg });
}
      });
    }
  }

  hasSecondField(category: any): boolean {
    return [1, 3, 4].includes(Number(category));
  }

 onFileSelected(event: any, index: number, inputRef: HTMLInputElement) {
  const file = event?.target?.files?.[0];
  if (!file) return;

  const check = validateFile(file);
  if (!check.valid) {
    this.messageService.add({ severity: 'error', summary: 'Invalid File', detail: check.message });
    inputRef.value = '';
    return;
  }

  const awGroup = this.awards.at(index) as FormGroup;
  (awGroup as any)._selectedFile = file;
  const localUrl = URL.createObjectURL(file);
  awGroup.get('fileUrl')?.setValue(localUrl);
  awGroup.get('fileUrl')?.markAsDirty();
  inputRef.value = '';
}
  getFileName(url: string | null): string {
    if (!url) return 'No file uploaded';
    try {
      const decoded = decodeURIComponent(url);
      return decoded.split('/').pop()?.split('?')[0] || 'Document';
    } catch {
      return 'Document';
    }
  }

  previewFile(url: string, name: string = 'Document Preview') {
    if (!url) return;
    this.originalFileUrl = url;
    this.previewFileName = name;
    this.isPdfLoading = true;
    // Blob URLs are local (file selected but not saved yet) — open directly
    // Remote URLs route through Google Docs Viewer
    let viewerUrl: string;
    if (url.startsWith('blob:')) {
      viewerUrl = url;
    } else {
      viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
    }
    this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl);
    this.displayPreviewDialog = true;
  }

  readonly isFieldInvalid = isFieldInvalid;

  private scrollToFirstError() {
    setTimeout(() => {
      const selectors = [
        'input.ng-invalid',
        'textarea.ng-invalid',
        'p-dropdown.ng-invalid',
        'p-calendar.ng-invalid',
        'p-autocomplete.ng-invalid',
        'p-selectbutton.ng-invalid'
      ];
      const el = document.querySelector(selectors.join(', '));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = el as HTMLElement;
        if (typeof focusable.focus === 'function') {
          focusable.focus();
        }
      }
    }, 100);
  }
}
